import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit , Output, ViewChild, } from '@angular/core';
import { faAngleDown, faAngleUp, faSearch } from '@fortawesome/pro-light-svg-icons';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, first } from 'rxjs/operators';
import { GroupedLocationInfo, LocationAvailabilityStatus, SelectedRollUpTab, } from 'search/models/search-results';
import { ConfigurationLoader } from 'shared/configuration-loader';
import { CustomerIntegrationType } from '../../../../customer-integration/customer-integration';
import { rollupTabCollapseLocationAndEditionInformation, tryToGetIt } from '../../../../entity/actions/entity.actions';
import { loadRelatedItemsOfMaterialTabEditions, queryEditions } from '../../../../entity/actions/full-entity.actions';
import {
  RollupSideDrawerContainer
} from '../../../../entity/components/rollup-side-drawer-container/rollup-side-drawer-container.component';
import {
  AvailabilityUpdateInfo,
  Edition,
  EditionType,
  EntityTypes,
  FormatGroup,
  GetItRequest,
  HoldRequest,
  MaterialTab,
} from '../../../../entity/models/entity';
import { getSelectedRollupTab } from '../../../../entity/reducers/entity.reducer';
import { selectEntity } from '../../../../entity/reducers/full-entity.reducer';
import { ItemLanguageMapperService } from 'app/services/item-language-mapper.service';
import { FeatureToggleService } from 'app/services/feature-toggle.service';
import { VendorLogo } from 'shared/models/configuration';


@Component({
  selector: 'app-rollup-tabs-content-card-header',
  templateUrl: './rollup-tabs-content-card-header.component.html',
  styleUrls: ['./rollup-tabs-content-card-header.component.scss'],
})
export class RollupTabsContentCardHeaderComponent implements OnInit, OnDestroy {
  @Input() public materialTab: MaterialTab;
  @Input() public availabilityUpdateInfo: AvailabilityUpdateInfo;
  @Input() public fgId: string;
  @Input() public fgTitle: string;
  @Input() public rollupTabIndex: number;
  @Input() public visible: boolean;
  @Input() public language: string;
  @Input() public hasEcontentIssues: boolean;
  @Output() public getIt = new EventEmitter(true);
  @Output() public getIt$ = new EventEmitter<{item: GetItRequest}>(true);
  @Output() public placeHold$ = new EventEmitter<{item: HoldRequest}>(true);
  @Output() public tableInited = new EventEmitter();
  @Output() public onEditionsSearch$ = new EventEmitter<string>();

  public tableExpanded: boolean;
  public holds: number;
  public copies: number;
  public instanceIds: string[];
  public vendorsLogo: { [key: string]: string };

  public isElectronic: boolean;
  public locations: GroupedLocationInfo[];
  public isNlbCustomer: boolean;
  public firstAvailableRecordId: string;
  public firstAvailableBarcode: string;

  public readonly arrowIconDown = faAngleDown;
  public readonly arrowIconUp = faAngleUp;
  public readonly searchIcon = faSearch;
  public editionsSearchInputDebouncer = new Subject<string>();
  public firstAvailableLanguage: string;
  public nonIntegratedLogo: { [key: string]: VendorLogo }


  @ViewChild('editionsSearchInput') private editionsSearchInput: ElementRef;

  @ViewChild('getItButton', { read: ElementRef }) private getItButton: ElementRef;
  private subscriptions = new Subscription();
  private hasOai: boolean;

  private featureHideNumberOfHolds = false;

  public constructor(
    private store: Store,
    private configLoader: ConfigurationLoader,
    private cdRef: ChangeDetectorRef,
    private modalService: NgbModal,
    private readonly featureToggleService: FeatureToggleService
  ) {
  }

  public ngOnInit() {
    this.vendorsLogo = this.configLoader.vendorsLogo;
    this.nonIntegratedLogo = this.configLoader.nonIntegratedConnectionsLogoMapping;
    this.isNlbCustomer = this.configLoader.customerIntegrationType === CustomerIntegrationType.NLB;
    this.isElectronic = EditionType.ELECTRONIC === this.materialTab.type;
    this.instanceIds = this.materialTab.editions?.map((instance) => instance.id);
    this.hasOai = this.isElectronic && this.materialTab.editions?.some(i => !!i.vendors?.oaipmh);
    this.firstAvailableRecordId = this.materialTab.itemEditionRecordId || this.materialTab.recordIds?.[0];
    const locationMap: { [key: string]: GroupedLocationInfo } = {};
    this.featureHideNumberOfHolds = this.featureToggleService.getToggles()['VDIS-26915_2025-12-30_scope-count-display'];
    this.materialTab.locations?.forEach(({ label, code, availabilityStatus }) => {
      const current: GroupedLocationInfo = locationMap[label] || { label, codes: [], availabilityStatus };
      current.codes.push(code);
      locationMap[label] = current;
      if (availabilityStatus === LocationAvailabilityStatus.Available) {
        locationMap[label].availabilityStatus = availabilityStatus;
      }
    }, locationMap);
    this.locations = Object.values(locationMap);

    this.subscriptions.add(this.store.pipe(
      select(getSelectedRollupTab(this.fgId)),
    ).pipe(
      debounceTime(300),
      first(),
    ).subscribe((selected: SelectedRollUpTab) => {
      if (this.shouldExpand(selected)) {
        this.tableExpanded = true;
        this.cdRef.markForCheck();
      }
    }));

    if (this.isNlbCustomer && !this.isElectronic) {
      this.subscriptions.add(this.store.select(selectEntity).subscribe((entity) => {
        if (entity?.entityType === EntityTypes.FORMAT_GROUP) {
          this.pickFirstAvailableBarcode((entity as FormatGroup).materialTabs[this.rollupTabIndex]?.editions);
        }
      }));
      this.store.dispatch(loadRelatedItemsOfMaterialTabEditions({materialTabIndex: this.rollupTabIndex}));
    }

    this.editionsSearchInputDebouncer.pipe(
      debounceTime(500)
    ).subscribe(value => {
      if (value !== null && value !== undefined) {
        this.store.dispatch(queryEditions({query:value}));
      }
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.store.dispatch(rollupTabCollapseLocationAndEditionInformation({
      resourceId: this.fgId,
      tabIndex: this.rollupTabIndex,
    }));
  }

  public toggleLocationAndEditionInfo() {
    this.tableExpanded = !this.tableExpanded;
  }
  public onGetIt(edition: any) {
    this.store.dispatch(tryToGetIt({
      item: edition,
    }));
  }

  public onCheckEditions() {
    this.tableExpanded = true;
  }

  public onTableInited() {
    this.tableInited.emit();
  }

  public onEditionsSearch(query: string) {
    if (query) {
      this.tableExpanded = true;
    }
    this.editionsSearchInputDebouncer.next(query);
  }

  public showQrCode(): boolean {
    return this.isNlbCustomer && !!(this.isElectronic
      ? this.availabilityUpdateInfo?.reserve?.id && !this.hasOai
      : this.firstAvailableRecordId);
  }

  public onCheckItemAvailability(location?: GroupedLocationInfo) {
    const getItButtonElement = this.getItButton && this.getItButton.nativeElement as HTMLElement;
    const modal = this.modalService.open(RollupSideDrawerContainer, {
      windowClass: 'drawer-modal',
      ariaLabelledBy: 'drawer-items-title'
    });
    modal.componentInstance.fgId = this.fgId;
    modal.componentInstance.tabName = this.materialTab.name;
    modal.componentInstance.title = this.fgTitle;
    modal.componentInstance.getItButtonTitle = getItButtonElement && getItButtonElement.textContent;
    modal.componentInstance.location = location;
    modal.componentInstance.showAllLocations = !location;
    modal.componentInstance.hideBlock = location ? 'step1' : 'step2';
    modal.componentInstance.isNlbCustomer = this.isNlbCustomer;
    modal.componentInstance.language = this.firstAvailableLanguage ?? this.language;

    modal.result.then((result) => {
      if (result === 'getIt') {
        const element = getItButtonElement && getItButtonElement.querySelector<HTMLElement>('button, a');
        element.click();
      }
    }, () => {});
  }

  private shouldExpand(selectedElement: SelectedRollUpTab) {
    return selectedElement && (selectedElement.selectedIndex === this.rollupTabIndex && selectedElement.expandLocationAndEditionInformation);
  }

  private pickFirstAvailableBarcode(editions: Edition[]) {
    const firstListedLocationCode = this.locations[0]?.codes?.[0];
    const itemMatchingLocation = editions[0]?.availability?.items.find(item => firstListedLocationCode === item?.locationCode);
    this.firstAvailableBarcode = itemMatchingLocation?.barcode;
    if (this.featureToggleService.getToggles()['VE-4791_2024-07-31_pass_item_language']) {
      this.firstAvailableLanguage = ItemLanguageMapperService.getLanguageByCode(itemMatchingLocation.language)?.name;
    }
  }

  public shouldShowAvailabilityInfo(): boolean {
    if (this.isElectronic) {
      return !!(this.availabilityUpdateInfo?.holds || this.availabilityUpdateInfo?.copies);
    }
    return !!(this.availabilityUpdateInfo?.holds || this.materialTab?.itemCount);
  }

  public getCopiesCount(): number {
    return this.isElectronic ? this.availabilityUpdateInfo?.copies : this.materialTab?.itemCount;
  }

  public getHoldsCount(): number {
    return this.availabilityUpdateInfo?.holds;
  }
}
